import { TrendingUp, Percent, WalletCards, Activity, RotateCcw, BarChart3, Send } from "lucide-react";
import { formatMoney, formatPercent } from "./pipelineHelpers";

const LOST_REASON_LABELS = {
  price:          "Price",
  time:           "Time",
  went_elsewhere: "Went elsewhere",
  changed_mind:   "Changed mind",
  other:          "Other",
};

// Revenue-pipeline KPIs at the top of /tx-plans. Real numbers from
// dos_v2.tx_plan_center_kpis() — no stubs.
//
// Layout: horizontal grid that wraps on narrow viewports. Each KPI is a
// small card with icon, headline value (bold tabular nums), and one
// subline of context (denominator / unit).
export default function KpiStrip({ kpis, loading, currency = "PHP" }) {
  const items = [
    {
      key: "proposed",
      label: "Proposals sent",
      Icon: Send,
      value: kpis ? formatMoney(kpis.proposed_value_centavos, currency) : "—",
      sub: kpis ? `${kpis.acceptance_proposed_count ?? 0} proposed` : "",
    },
    {
      key: "pipeline",
      label: "Accepted pipeline",
      Icon: TrendingUp,
      value: kpis ? formatMoney(kpis.pipeline_value_centavos, currency) : "—",
      sub: "accepted, not closed",
    },
    {
      key: "active",
      label: "Active treatment",
      Icon: Activity,
      value: kpis ? formatMoney(kpis.active_value_centavos, currency) : "—",
      sub: "scheduled + in progress",
    },
    {
      key: "ar",
      label: "AR outstanding",
      Icon: WalletCards,
      value: kpis ? formatMoney(kpis.ar_value_centavos, currency) : "—",
      sub: "completed work not collected",
    },
    {
      key: "recovery",
      label: "Recoverable",
      Icon: RotateCcw,
      value: kpis ? formatMoney(kpis.recovery_value_centavos, currency) : "—",
      sub: "paused · expired · needs booking · abandoned",
    },
    {
      key: "rate",
      label: "Acceptance rate · 30d",
      Icon: Percent,
      value: kpis ? formatPercent(kpis.acceptance_rate_pct) : "—",
      sub: kpis
        ? `${kpis.acceptance_accepted_count ?? 0} accepted / ${kpis.acceptance_proposed_count ?? 0} proposed`
        : "",
    },
  ];

  const lostReasons = kpis?.lost_reasons_90d || [];
  const lostMax = lostReasons.reduce((m, r) => Math.max(m, r.count || 0), 0);

  return (
    <div data-testid="tx-plan-center-kpis" data-tutorial-id="tx-plan-center-kpis" className="space-y-2.5">
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-2.5">
        {items.map(({ key, label, Icon, value, sub }) => (
          <div
            key={key}
            className="rounded-lg border border-slate-200 bg-white px-3.5 py-3 flex flex-col gap-1"
          >
            <div className="flex items-center gap-1.5 text-slate-500">
              <Icon className="w-3.5 h-3.5" strokeWidth={2.25} />
              <span className="text-[11px] uppercase tracking-wide font-medium">{label}</span>
            </div>
            <div className={[
              "text-[20px] font-bold tabular-nums text-slate-900",
              loading ? "opacity-50" : "",
            ].join(" ")}>
              {value}
            </div>
            <div className="text-[11px] text-slate-500 truncate">{sub || " "}</div>
          </div>
        ))}
      </div>

      {lostReasons.length > 0 ? (
        <div className="rounded-lg border border-slate-200 bg-white px-3.5 py-3">
          <div className="flex items-center gap-1.5 text-slate-500 mb-2">
            <BarChart3 className="w-3.5 h-3.5" strokeWidth={2.25} />
            <span className="text-[11px] uppercase tracking-wide font-medium">
              Why plans were lost · last 90 days
            </span>
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-1.5">
            {lostReasons.map(({ reason, count }) => {
              const pct = lostMax > 0 ? Math.round((count / lostMax) * 100) : 0;
              return (
                <li key={reason} className="text-[12px]">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-slate-700 truncate">
                      {LOST_REASON_LABELS[reason] || reason}
                    </span>
                    <span className="font-semibold tabular-nums text-slate-900 shrink-0">{count}</span>
                  </div>
                  <div className="mt-0.5 h-1 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full bg-rose-400/70" style={{ width: `${pct}%` }} />
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
